<template>
    <v-form class="pb-4" data-cy="description-details-form">
        <v-row dense class="mb-4">
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('description') }}
                </span>
            </v-col>
            <v-col cols="12" md="10">
                <v-row v-if="isShowEditors && useDescriptions?.length && !isLoading" no-gutters class="px-1">
                    <v-col v-for="{description, type} in useDescriptions" :key="type" cols="12" class="mb-2">
                        <NewHtmlEditor
                            v-model="description[activeLangCode]"
                            :toolbar="isHotelDescriptionMode ? {items: []} : undefined"
                            :label="$t(`descriptions.${type}`)"
                            :readonly="isHotelDescriptionMode"
                            class="mb-2"
                        >
                            <div
                                v-if="activeLangCode"
                                class="fi fill-height px-3"
                                :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                            />
                        </NewHtmlEditor>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row v-if="Object.keys(filteredClassifications).length" dense class="mb-6">
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('classifications.POLICIES') }}
                </span>
            </v-col>
            <v-col cols="12" md="10">
                <v-row dense>
                    <v-col v-for="(group, index) in filteredClassifications" :key="index" cols="12" sm="3" md="2">
                        <div v-for="{id, name} in group" :key="id">
                            <v-checkbox
                                v-model="selectedClassifications"
                                hide-details
                                :label="name"
                                :value="id"
                                density="compact"
                                :data-cy="id"
                                :readonly="isHotelDescriptionMode"
                            />
                        </div>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn v-if="!isHotelDescriptionMode" :loading="isSaving" :disabled="isLoading || isSaving" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn
                class="cancel-btn"
                data-cy="cancel"
                :disabled="isLoading || isSaving"
                :to="{name: 'supplierProducts'}"
                variant="text"
            >
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {useFlagIconCode} from '~/composables/helpers'
    const NewHtmlEditor = defineAsyncComponent(() => import('~/components/admin/NewHtmlEditor.vue'))

    const isLoading = ref<boolean>(false)
    const isSaving = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const isShowEditors = ref<boolean>(false)
    const descriptions = ref([])
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
    const hotelDescriptionId: string = inject('hotelDescriptionId', null)
    const classifications = ref(null)
    const selectedClassifications = ref<number[]>([])
    const generalFormData = ref(null)

    const filteredClassifications = computed(() => {
        if (!classifications.value) return {}
        const result: Record<string, any[]> = {}

        const POLICY_CLASSIFICATION_NAMES = ['Only Adults', 'Pets Friendly']
        const policyItems: any[] = []
        for (const items of Object.values(classifications.value) as any[][]) {
            for (const item of items) {
                if (POLICY_CLASSIFICATION_NAMES.some(name => item.name === name)) {
                    policyItems.push(item)
                }
            }
        }
        if (policyItems.length) {
            result['POLICIES'] = policyItems
        }

        return result
    })

    const app = useNuxtApp()
    const {t} = useI18n()

    watch(useHotelInfo, async () => {
        await loadData()
        loadClassifications()
    })

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
        if (supplierStore.fullContract?.hotelContentId || isHotelDescriptionMode) {
            await loadData()
            loadClassifications()
        }
        EventBus.$on(CHANGE_LOCALE_EVENT, reloadClassifications)
    })

    onBeforeUnmount(() => {
        EventBus.$off(CHANGE_LOCALE_EVENT, reloadClassifications)
    })

    async function reloadClassifications(): Promise<void> {
        loadClassificationDictionary()
    }

    async function loadData(): Promise<void> {
        const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId
        try {
            isLoading.value = true
            const {hotelDescriptionDetails} = await app.$api.hotelDescriptionDetails.get(hotelId, {locale: 'all'})
            descriptions.value = hotelDescriptionDetails
            isShowEditors.value = true
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    async function loadClassifications(): Promise<void> {
        const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId
        try {
            const res = await app.$api.hotelDescriptionGeneral.get(hotelId, {locale: 'all'})
            generalFormData.value = res
            selectedClassifications.value = res.classifications || []
        } catch (_e) {
            // classifications will remain empty
        }
        loadClassificationDictionary()
    }

    async function loadClassificationDictionary(): Promise<void> {
        try {
            const res = await app.$api.hotelClassificationDictionaryInfo.get({
                supplierId: supplierStore.hotelInfo?.supplierId || 1,
            })
            const groupedArray = res.reduce((acc: Record<string, any[]>, curr: any) => {
                if (!acc[curr.groupCode]) {
                    acc[curr.groupCode] = []
                }
                acc[curr.groupCode].push(curr)
                return acc
            }, {})
            const desiredOrder = ['LOCATION', 'BUDGET', 'TARGET', 'ARCHITECTURE', 'SUSTAINABILITY_PRACTICES', 'OTHER']
            const keys = Object.keys(groupedArray)
            const sortedKeys = keys?.sort((a: string, b: string) => desiredOrder.indexOf(a) - desiredOrder.indexOf(b))
            const sortedObject: Record<string, any[]> = {}
            sortedKeys.forEach(key => {
                sortedObject[key] = groupedArray[key]?.sort((a: any, b: any) => a.name.localeCompare(b.name))
            })
            classifications.value = sortedObject
        } catch (_e) {
            app.$toast.error(t('error_message.load_error'))
        }
    }

    const save = async (): Promise<void> => {
        try {
            isSaving.value = true
            const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId
            await app.$api.hotelDescriptionDetails.put(hotelId, {
                hotelDescriptionDetails: descriptions.value,
            })

            if (generalFormData.value) {
                await app.$api.hotelDescriptionGeneral.put(hotelId, {
                    ...generalFormData.value,
                    classifications: selectedClassifications.value,
                })
            }

            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isSaving.value = false
        }
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
        flickEditors()
    }

    const flickEditors = (): void => {
        isShowEditors.value = false
        nextTick().then(() => {
            isShowEditors.value = true
        })
    }

    const useDescriptions = computed(() => {
        // Only show General and Child Policies (other) fields as per client request
        const dscriptionTypes = ['general', 'other']
        const baseDescriptions = dscriptionTypes.map(item => ({type: item, description: {}}))
        if (descriptions.value) {
            const mergedDescriptions = baseDescriptions.map(base => {
                const match = descriptions.value.find(item => item.type === base.type)
                if (match) {
                    return {...base, description: match.description}
                }
                return base
            })
            return mergedDescriptions
        } else return baseDescriptions
    })

    watch(useDescriptions, async () => {
        if (useDescriptions.value.length) {
            flickEditors()
        }
    })
</script>
